import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Typography,
  useTheme,
  Tooltip,
  Backdrop,
  IconButton,
} from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Legend,
} from "chart.js";
import { memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Line } from "react-chartjs-2";
import OneViewBox from "../../../components/layouts/OneViewBox";
import LEFTARROWICON from "../../../assets/images/leftIcon.svg";
import LetterAIcon from "../../../assets/images/letter-a.svg";
import RIGHTARROWICON from "../../../assets/images/rightarrow.svg";
import ARROW_RIGHT from "../../../assets/images/arrow-right (4).png";
import ARROW_LEFT from "../../../assets/images/arrow-right (5).png";
import RESET_LEFT from "../../../assets/images/arrow-right (6).png";
import RESET_RIGHT from "../../../assets/images/arrow-right (7).png";
import ucl_left from "../../../assets/images/ucl_left.svg";
import ucl_right from "../../../assets/images/ucl_right.svg";
import mcl_left from "../../../assets/images/mcl_left.svg";
import mcl_right from "../../../assets/images/mcl_right.svg";
import { OscillaChartComponent } from "./OscillaChartComponent";
import annotationPlugin from "chartjs-plugin-annotation";
import AirNotHeardLeftIMG from "../../../assets/images/AudiogramImages/AirNotHeardLeft.svg";
import AirNotHeardRightIMG from "../../../assets/images/AudiogramImages/AirNotHeardRight.svg";
import BoneNotHeardLeftIMG from "../../../assets/images/AudiogramImages/BoneNotHeardLeft.svg";
import BoneNotHeardRightIMG from "../../../assets/images/AudiogramImages/BoneNotHeardRight.svg";
import ACHeardLeftIMG from "../../../assets/images/ac-left.svg";
import AcHeardRightIMG from "../../../assets/images/ac-right.svg";
import { startOscillaLoadingValues } from "../../../store/actions/OscillaDataAction";
import CloseIcon from "@mui/icons-material/Close";
import ReactDOM from "react-dom";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Legend,
  annotationPlugin,
);

const dottedGridPlugin = {
  id: "dottedGridPlugin",
  beforeDatasetsDraw(chart, args, pluginOptions) {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea) return;

    const xScale = scales.x;
    const yScale = scales.y;
    const xTargets = (pluginOptions && pluginOptions.xTargets) || [
      "750",
      "1.5k",
      "3k",
      "6k",
    ];

    ctx.save();

    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 0.6;
    ctx.strokeStyle = (pluginOptions && pluginOptions.xColor) || "#999";

    xTargets.forEach((lbl) => {
      const idx = xScale.ticks.findIndex((t) => t.label === lbl);
      if (idx !== -1) {
        const x = xScale.getPixelForTick(idx);
        ctx.beginPath();
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.stroke();
      }
    });

    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = (pluginOptions && pluginOptions.yColor) || "#bbb";

    ctx.restore();
  },
};

const useLoadedImage = (src, width, height) => {
  const [img, setImg] = useState(null);

  useEffect(() => {
    const image = new Image();
    image.src = src;
    image.width = width;
    image.height = height;
    image.onload = () => setImg(image);
  }, [src, width, height]);

  return img;
};

const ChartComponent = ({
  data,
  title,
  onValueChange,
  boneData = [],
  boneTitle = "",
  UclTitle = "",
  MclTitle = "",
  AidedTitle = "",
  aidedData,
  UclData = [],
  MclData = [],
  rightEar,
  allowed_ac,
  allowed_bc,
  allowed_ucl,
  allowed_mcl,
  allowed_aided,
  mode,
  bananaAlphabets,
  n_heardBC,
  n_heardAC,
}) => {
  const theme = useTheme();
  const [editingIndex, setEditingIndex] = useState(undefined);

  const labels = [
    "125",
    "250",
    "500",
    "750",
    "1k",
    "1.5k",
    "2k",
    "3k",
    "4k",
    "6k",
    "8k",
  ];
  const boneImg = new Image();
  if (rightEar) {
    boneImg.src = RIGHTARROWICON;
  } else {
    boneImg.src = LEFTARROWICON;
  }
  boneImg.height = 8;
  boneImg.width = 4;

  const UclImg = new Image();
  if (rightEar) {
    UclImg.src = ucl_right;
  } else {
    UclImg.src = ucl_left;
  }
  UclImg.height = 16;
  UclImg.width = 16;

  const MclImg = new Image();
  if (rightEar) {
    MclImg.src = mcl_right;
  } else {
    MclImg.src = mcl_left;
  }
  MclImg.height = 16;
  MclImg.width = 16;

  const aidedImg = new Image();
  aidedImg.src = LetterAIcon;

  const BoneHeardIMG = useLoadedImage(
    rightEar ? RIGHTARROWICON : LEFTARROWICON,
    12,
    12,
  );
  const BoneNotHeardIMG = useLoadedImage(
    rightEar ? BoneNotHeardRightIMG : BoneNotHeardLeftIMG,
    22,
    22,
  );
  const AirHeardIMG = useLoadedImage(
    rightEar ? AcHeardRightIMG : ACHeardLeftIMG,
    12,
    12,
  );
  const AirNotHeardIMG = useLoadedImage(
    rightEar ? AirNotHeardRightIMG : AirNotHeardLeftIMG,
    16,
    16,
  );

  const filterPoints = (labels, data) => {
    const points = [];
    labels.forEach((label, idx) => {
      if (data && data[idx] !== null && data[idx] !== undefined) {
        points.push({
          x: label,
          y: Number(data[idx]),
        });
      }
    });
    return points;
  };
  const splitConsecutiveSegments = (labels, points, notHeardLabels) => {
    const segments = [];
    let currentSeg = [];
    points.forEach((pt, idx) => {
      const labelIdx = labels.indexOf(pt.x);
      if (
        currentSeg.length > 0 &&
        labels
          .slice(
            labels.indexOf(currentSeg[currentSeg.length - 1].x) + 1,
            labelIdx,
          )
          .some((l) => notHeardLabels.has(l))
      ) {
        segments.push([...currentSeg]);
        currentSeg = [pt];
      } else {
        currentSeg.push(pt);
      }
    });
    if (currentSeg.length) segments.push(currentSeg);
    return segments;
  };

  const ACPoints = filterPoints(labels, data);
  const nHeardACPoints = filterPoints(labels, n_heardAC);
  const BCPoints = filterPoints(labels, boneData);
  const nHeardBCPoints = filterPoints(labels, n_heardBC);
  const UCLPoints = filterPoints(labels, UclData);
  const MCLPoints = filterPoints(labels, MclData);
  const AidedPoints = filterPoints(labels, aidedData);

  const nHeardACLabels = new Set(nHeardACPoints.map((p) => p.x));
  const nHeardBCLabels = new Set(nHeardBCPoints.map((p) => p.x));

  const heardACSegments = splitConsecutiveSegments(
    labels,
    ACPoints,
    nHeardACLabels,
  );

  const heardBCSegments = splitConsecutiveSegments(
    labels,
    BCPoints,
    nHeardBCLabels,
  );

  const datasets = [];

  if (allowed_ac) {
    if (heardACSegments.length) {
      heardACSegments.forEach((seg) => {
        datasets.push({
          label: "AC Threshold",
          data: seg,
          parsing: false,
          pointBackgroundColor: "white",
          pointStyle: AirHeardIMG,
          borderColor: rightEar ? "#E62323" : "#1265A1",
          radius: rightEar ? 4 : 7,
          showLine: true,
        });
      });
    }

    if (nHeardACPoints.length) {
      nHeardACPoints.forEach((pt) => {
        datasets.push({
          label: "AC Not Heard",
          data: [pt],
          parsing: false,
          pointBackgroundColor: "white",
          pointStyle: AirNotHeardIMG,
          borderColor: rightEar ? "#E62323" : "#1265A1",
          radius: rightEar ? 4 : 7,
          showLine: false,
        });
      });
    }
  }

  if (allowed_aided && AidedPoints.length) {
    datasets.push({
      label: AidedTitle,
      data: AidedPoints,
      pointBackgroundColor: "white",
      pointStyle: aidedImg,
      hoverBorderColor: rightEar ? "#C24747" : "#1265A1",
      borderColor: rightEar ? "#C24747" : "#1265A1",
      radius: rightEar ? 4 : 7,
      hoverRadius: rightEar ? 4 : 7,
      borderWidth: 1,
      hoverBorderWidth: 1,
      tension: 0,
    });
  }

  if (allowed_bc) {
    if (BCPoints.length) {
      heardBCSegments.forEach((seg) => {
        datasets.push({
          label: "BC Threshold",
          data: seg,
          parsing: false,
          pointBackgroundColor: "white",
          pointStyle: BoneHeardIMG,
          borderColor: rightEar ? "#E62323" : "#1265A1",
          radius: rightEar ? 4 : 7,
          showLine: true,
          borderDash: [10, 5],
        });
      });
    }
    if (nHeardBCPoints.length) {
      nHeardBCPoints.forEach((pt) => {
        datasets.push({
          label: "BC Not Heard",
          data: [pt],
          parsing: false,
          pointBackgroundColor: "white",
          pointStyle: BoneNotHeardIMG,
          borderColor: rightEar ? "#E62323" : "#1265A1",
          radius: rightEar ? 4 : 7,
          showLine: false,
        });
      });
    }
  }

  if (allowed_ucl && UCLPoints.length) {
    datasets.push({
      label: UclTitle,
      data: UCLPoints,
      pointBackgroundColor: "white",
      pointStyle: UclImg,
      hoverBorderColor: rightEar ? "#C24747" : "#1265A1",
      borderColor: rightEar ? "#C24747" : "#1265A1",
      radius: rightEar ? 4 : 7,
      hoverRadius: rightEar ? 4 : 7,
      borderWidth: 1,
      hoverBorderWidth: 1,
      tension: 0,
    });
  }

  if (allowed_mcl && MCLPoints.length) {
    datasets.push({
      label: MclTitle,
      data: MCLPoints,
      pointBackgroundColor: "white",
      pointStyle: MclImg,
      hoverBorderColor: rightEar ? "#C24747" : "#1265A1",
      borderColor: rightEar ? "#C24747" : "#1265A1",
      radius: rightEar ? 4 : 7,
      hoverRadius: rightEar ? 4 : 7,
      borderWidth: 1,
      hoverBorderWidth: 1,
      tension: 0,
    });
  }

  const dataFields = {
    labels,
    datasets,
  };

  return (
    <Line
      data={dataFields}
      plugins={[dottedGridPlugin]}
      options={{
        aspectRatio: 1.05,
        interaction: {
          mode: "nearest",
          intersect: false, // Ensures the tooltip appears on hover anywhere
        },
        plugins: {
          datalabels: {
            display: 0,
            backgroundColor: "red",
          },
          tooltip: {
            enabled: true,
            mode: "nearest",
            intersect: false,
            yAlign: "top",
            xAlign: "center",
            caretPadding: 6,
            callbacks: {
              label: function (context) {
                let label = context.dataset.label || "";
                if (label) {
                  label += ": ";
                }
                if (context.parsed.y !== null) {
                  // check if it's -0 or any equivalent value and parse it as 0
                  label += context.parsed.y === 0 ? 0 : context.parsed.y;
                }
                return label;
              },
            },
          },
          legend: {
            display: false,
          },
        },
        animation: false,
        elements: {
          point: {
            radius: 0, // Hide point hover effects
          },
          line: {
            borderWidth: 1,
            tension: 0, // Ensure lines are straight
          },
        },
        scales: {
          y: {
            title: {
              display: true,
              text: "Threshold (dB HL)",
              color: "#3C508B",
              font: {
                size: 10,
              },
            },
            min: -10,
            max: 120,
            reverse: "true",
            ticks: {
              callback: (value) => value,
              font: {
                size: 10,
              },
              stepSize: 10,
            },
          },
          x: {
            title: {
              display: true,
              text: "Frequency (Hz)",
              color: "#3C508B",
              font: {
                size: 10,
              },
            },
            grid: {
              display: true,
              lineWidth: 1,
              color: (ctx) => {
                const lbl = ctx.tick && ctx.tick.label;
                if (["750", "1.5k", "3k", "6k"].includes(lbl))
                  return "transparent";
                return "#ccc";
              },
            },
            ticks: {
              font: {
                size: 10,
                color: "#666666",
              },
            },
          },
        },
        onClick: (e, ele, chart) => {
          const xValue = chart.scales.x.getValueForPixel(e.x);
          const yValue = chart.scales.y.getValueForPixel(e.y);
          const nearestIndex = Math.round(xValue);
          const isCtrlPressed = e.native.ctrlKey || e.native.metaKey;

          if (nearestIndex >= 0 && nearestIndex < labels.length) {
            const newValue = Math.round(yValue / 5) * 5;

            if (newValue >= 0 && newValue <= 120) {
              onValueChange(nearestIndex, newValue, mode, isCtrlPressed);
              setEditingIndex(nearestIndex); // set the index to edit
            } else if (newValue < 0) {
              onValueChange(nearestIndex, -10, mode, isCtrlPressed);
              setEditingIndex(nearestIndex); // set the index to edit
            } else {
              onValueChange(nearestIndex, 120, mode, isCtrlPressed);
              setEditingIndex(nearestIndex); // set the index to edit
            }
          }
        },
        onHover: (e, chartElement, chart) => {
          const tooltip = chart.tooltip;

          if (chartElement && chartElement.length) {
            chart.canvas.style.cursor = "pointer";
            tooltip.setActiveElements(chartElement, {
              x: e.x,
              y: e.y,
            });
            tooltip.update();
          } else {
            chart.canvas.style.cursor = "default";
            tooltip.setActiveElements([], {});
            tooltip.update();
          }
        },
        onLeave: (e, chart) => {
          chart.canvas.style.cursor = "default";
          const tooltip = chart.tooltip;
          tooltip.setActiveElements([], {});
          tooltip.update(true);
          chart.draw();
        },
      }}
    />
  );
};

const AudioGram = ({
  audiogramState,
  value,
  setValue,
  only_view,
  fields,
  setFields,
  loading,
  no_frequncy_box,
  setOscillaAudiogramState,
  oscillaAudiogramState,
  bananaAlphabets,
}) => {
  const { testStarted } = useSelector((state) => state.oscilla);
  const dispatch = useDispatch();
  // useEffect(() => { setOscillaAudiogramState({ allowed_Oscilla: AudiosGramData(data).length > 0 ? true : false, ...data }); }, [data]);

  const [mode, setMode] = useState("ac");
  const [oscillaMode, setOscillaMode] = useState("ac Heard");
  const [rightAcData, setRightAcData] = useState([
    fields.rf1,
    fields.rf2,
    fields.rf3,
    fields?.rf3_5,
    fields.rf4,
    fields?.rf4_5,
    fields.rf5,
    fields?.rf5_5,
    fields.rf6,
    fields?.rf6_5,
    fields.rf7,
  ]); // Initialize right AC data with null values
  const [rightBcData, setRightBcData] = useState([
    fields.brf1,
    fields.brf2,
    fields.brf3,
    fields?.brf3_5,
    fields.brf4,
    fields?.brf4_5,
    fields.brf5,
    fields?.brf5_5,
    fields.brf6,
    fields?.brf6_5,
    fields.brf7,
  ]); // Initialize right BC data with null values
  const [rightUclData, setRightUclData] = useState([
    fields.urf1,
    fields.urf2,
    fields.urf3,
    fields?.urf3_5,
    fields.urf4,
    fields?.urf4_5,
    fields.urf5,
    fields?.urf5_5,
    fields.urf6,
    fields?.urf6_5,
    fields.urf7,
  ]); // Initialize right BC data with null values
  const [rightMclData, setRightMclData] = useState([
    fields.mrf1,
    fields.mrf2,
    fields.mrf3,
    fields?.mrf3_5,
    fields.mrf4,
    fields?.mrf4_5,
    fields.mrf5,
    fields?.mrf5_5,
    fields.mrf6,
    fields?.mrf6_5,
    fields.mrf7,
  ]); // Initialize right BC data with null values
  const [leftAcData, setLeftAcData] = useState([
    fields.lf1,
    fields.lf2,
    fields.lf3,
    fields?.lf3_5,
    fields.lf4,
    fields?.lf4_5,
    fields.lf5,
    fields?.lf5_5,
    fields.lf6,
    fields?.lf6_5,
    fields.lf7,
  ]); // Initialize left AC data with null values
  const [leftUclData, setLeftUclData] = useState([
    fields.ulf1,
    fields.ulf2,
    fields.ulf3,
    fields?.ulf3_5,
    fields.ulf4,
    fields?.ulf4_5,
    fields.ulf5,
    fields?.ulf5_5,
    fields.ulf6,
    fields?.ulf6_5,
    fields.ulf7,
  ]); // Initialize left AC data with null values
  const [leftBcData, setLeftBcData] = useState([
    fields.blf1,
    fields.blf2,
    fields.blf3,
    fields?.blf3_5,
    fields.blf4,
    fields?.blf4_5,
    fields.blf5,
    fields?.blf5_5,
    fields.blf6,
    fields?.blf6_5,
    fields.blf7,
  ]); // Initialize left BC data with null values
  const [leftMclData, setLeftMclData] = useState([
    fields.mlf1,
    fields.mlf2,
    fields.mlf3,
    fields?.mlf3_5,
    fields.mlf4,
    fields?.mlf4_5,
    fields.mlf5,
    fields?.mlf5_5,
    fields.mlf6,
    fields?.mlf6_5,
    fields.mlf7,
  ]); // Initialize left BC data with null values

  const [leftAidedData, setLeftAidedData] = useState([
    fields.aided_lf1,
    fields.aided_lf2,
    fields.aided_lf3,
    fields?.aided_lf3_5,
    fields.aided_lf4,
    fields?.aided_lf4_5,
    fields.aided_lf5,
    fields?.aided_lf5_5,
    fields.aided_lf6,
    fields?.aided_lf6_5,
    fields.aided_lf7,
  ]); // Initialize left AC data with null values
  const [rightAidedData, setRightAidedData] = useState([
    fields.aided_rf1,
    fields.aided_rf2,
    fields.aided_rf3,
    fields?.aided_rf3_5,
    fields.aided_rf4,
    fields?.aided_rf4_5,
    fields.aided_rf5,
    fields?.aided_rf5_5,
    fields.aided_rf6,
    fields?.aided_rf6_5,
    fields.aided_rf7,
  ]); // Initialize left AC data with null values

  const handleMirrorData_r_to_l = () => {
    setFields({
      ...fields,
      lf1: fields?.rf1,
      lf2: fields?.rf2,
      lf3: fields?.rf3,
      lf3_5: fields?.rf3_5,
      lf4: fields?.rf4,
      lf4_5: fields?.rf4_5,
      lf5: fields?.rf5,
      lf5_5: fields?.rf5_5,
      lf6: fields?.rf6,
      lf6_5: fields?.rf6_5,
      lf7: fields?.rf7,
      // lf8: fields?.rf8,

      blf1: fields?.brf1,
      blf2: fields?.brf2,
      blf3: fields?.brf3,
      blf3_5: fields?.brf3_5,
      blf4: fields?.brf4,
      blf4_5: fields?.brf4_5,
      blf5: fields?.brf5,
      blf5_5: fields?.brf5_5,
      blf6: fields?.brf6,
      blf6_5: fields?.brf6_5,
      blf7: fields?.brf7,
      blf8: fields?.brf8,

      ulf1: fields?.urf1,
      ulf2: fields?.urf2,
      ulf3: fields?.urf3,
      ulf3_5: fields?.urf3_5,
      ulf4: fields?.urf4,
      ulf4_5: fields?.urf4_5,
      ulf5: fields?.urf5,
      ulf5_5: fields?.urf5_5,
      ulf6: fields?.urf6,
      ulf6_5: fields?.urf6_5,
      ulf7: fields?.urf7,
      ulf8: fields?.urf8,

      mlf1: fields?.mrf1,
      mlf2: fields?.mrf2,
      mlf3: fields?.mrf3,
      mlf3_5: fields?.mrf3_5,
      mlf4: fields?.mrf4,
      mlf4_5: fields?.mrf4_5,
      mlf5: fields?.mrf5,
      mlf5_5: fields?.mrf5_5,
      mlf6: fields?.mrf6,
      mlf6_5: fields?.mrf6_5,
      mlf7: fields?.mrf7,
      mlf8: fields?.mrf8,

      aided_lf1: fields?.aided_rf1,
      aided_lf2: fields?.aided_rf2,
      aided_lf3: fields?.aided_rf3,
      aided_lf3_5: fields?.aided_rf3_5,
      aided_lf4: fields?.aided_rf4,
      aided_lf4_5: fields?.aided_rf4_5,
      aided_lf5: fields?.aided_rf5,
      aided_lf5_5: fields?.aided_rf5_5,
      aided_lf6: fields?.aided_rf6,
      aided_lf6_5: fields?.aided_rf6_5,
      aided_lf7: fields?.aided_rf7,
      aided_lf8: fields?.aided_rf8,

      ac_l_125_nh: fields.ac_r_125_nh,
      ac_l_250_nh: fields.ac_r_250_nh,
      ac_l_500_nh: fields.ac_r_500_nh,
      ac_l_750_nh: fields.ac_r_750_nh,
      ac_l_1000_nh: fields.ac_r_1000_nh,
      ac_l_1500_nh: fields.ac_r_1500_nh,
      ac_l_2000_nh: fields.ac_r_2000_nh,
      ac_l_3000_nh: fields.ac_r_3000_nh,
      ac_l_4000_nh: fields.ac_r_4000_nh,
      ac_l_6000_nh: fields.ac_r_6000_nh,
      ac_l_8000_nh: fields.ac_r_8000_nh,

      bc_l_125_nh: fields.bc_r_125_nh,
      bc_l_250_nh: fields.bc_r_250_nh,
      bc_l_500_nh: fields.bc_r_500_nh,
      bc_l_750_nh: fields.bc_r_750_nh,
      bc_l_1000_nh: fields.bc_r_1000_nh,
      bc_l_1500_nh: fields.bc_r_1500_nh,
      bc_l_2000_nh: fields.bc_r_2000_nh,
      bc_l_3000_nh: fields.bc_r_3000_nh,
      bc_l_4000_nh: fields.bc_r_4000_nh,
      bc_l_6000_nh: fields.bc_r_6000_nh,
      bc_l_8000_nh: fields.bc_r_8000_nh,
    });
    setLeftAcData(rightAcData);
    setLeftBcData(rightBcData);
    setLeftUclData(rightUclData);
    setLeftMclData(rightMclData);
    setLeftAidedData(rightAidedData);
  };

  const handleMirrorData_l_to_r = () => {
    setFields({
      ...fields,
      rf1: fields?.lf1,
      rf2: fields?.lf2,
      rf3: fields?.lf3,
      rf3_5: fields?.lf3_5,
      rf4: fields?.lf4,
      rf4_5: fields?.lf4_5,
      rf5: fields?.lf5,
      rf5_5: fields?.lf5_5,
      rf6: fields?.lf6,
      rf6_5: fields?.lf6_5,
      rf7: fields?.lf7,
      // rf8: fields?.lf8,

      brf1: fields?.blf1,
      brf2: fields?.blf2,
      brf3: fields?.blf3,
      brf3_5: fields?.blf3_5,
      brf4: fields?.blf4,
      brf4_5: fields?.blf4_5,
      brf5: fields?.blf5,
      brf5_5: fields?.blf5_5,
      brf6: fields?.blf6,
      brf6_5: fields?.blf6_5,
      brf7: fields?.blf7,
      brf8: fields?.blf8,

      urf1: fields?.ulf1,
      urf2: fields?.ulf2,
      urf3: fields?.ulf3,
      urf3_5: fields?.ulf3_5,
      urf4: fields?.ulf4,
      urf4_5: fields?.ulf4_5,
      urf5: fields?.ulf5,
      urf5_5: fields?.ulf5_5,
      urf6: fields?.ulf6,
      urf6_5: fields?.ulf6_5,
      urf7: fields?.ulf7,
      urf8: fields?.ulf8,

      mrf1: fields?.mlf1,
      mrf2: fields?.mlf2,
      mrf3: fields?.mlf3,
      mrf3_5: fields?.mlf3_5,
      mrf4: fields?.mlf4,
      mrf4_5: fields?.mlf4_5,
      mrf5: fields?.mlf5,
      mrf5_5: fields?.mlf5_5,
      mrf6: fields?.mlf6,
      mrf6_5: fields?.mlf6_5,
      mrf7: fields?.mlf7,
      mrf8: fields?.mlf8,

      aided_rf1: fields?.aided_lf1,
      aided_rf2: fields?.aided_lf2,
      aided_rf3: fields?.aided_lf3,
      aided_rf3_5: fields?.aided_lf3_5,
      aided_rf4: fields?.aided_lf4,
      aided_rf4_5: fields?.aided_lf4_5,
      aided_rf5: fields?.aided_lf5,
      aided_rf5_5: fields?.aided_lf5_5,
      aided_rf6: fields?.aided_lf6,
      aided_rf6_5: fields?.aided_lf6_5,
      aided_rf7: fields?.aided_lf7,
      aided_rf8: fields?.aided_lf8,

      ac_r_125_nh: fields.ac_l_125_nh,
      ac_r_250_nh: fields.ac_l_250_nh,
      ac_r_500_nh: fields.ac_l_500_nh,
      ac_r_750_nh: fields.ac_l_750_nh,
      ac_r_1000_nh: fields.ac_l_1000_nh,
      ac_r_1500_nh: fields.ac_l_1500_nh,
      ac_r_2000_nh: fields.ac_l_2000_nh,
      ac_r_3000_nh: fields.ac_l_3000_nh,
      ac_r_4000_nh: fields.ac_l_4000_nh,
      ac_r_6000_nh: fields.ac_l_6000_nh,
      ac_r_8000_nh: fields.ac_l_8000_nh,

      bc_r_125_nh: fields.bc_l_125_nh,
      bc_r_250_nh: fields.bc_l_250_nh,
      bc_r_500_nh: fields.bc_l_500_nh,
      bc_r_750_nh: fields.bc_l_750_nh,
      bc_r_1000_nh: fields.bc_l_1000_nh,
      bc_r_1500_nh: fields.bc_l_1500_nh,
      bc_r_2000_nh: fields.bc_l_2000_nh,
      bc_r_3000_nh: fields.bc_l_3000_nh,
      bc_r_4000_nh: fields.bc_l_4000_nh,
      bc_r_6000_nh: fields.bc_l_6000_nh,
      bc_r_8000_nh: fields.bc_l_8000_nh,
    });
    setRightAcData(leftAcData);
    setRightBcData(leftBcData);
    setRightUclData(leftUclData);
    setRightMclData(leftMclData);
    setRightAidedData(leftAidedData);
  };

  const handleToggleAC = () => {
    if (mode === "ac") {
      setFields((prevFields) => ({
        ...prevFields,
        allowed_ac: !prevFields?.allowed_ac,
      }));
    } else {
      setMode("ac");
      setFields((prevFields) => ({
        ...prevFields,
        allowed_ac: true,
      }));
    }
  };

  const handleToggleBC = () => {
    if (mode === "bc") {
      setFields((prevFields) => ({
        ...prevFields,
        allowed_bc: !prevFields?.allowed_bc,
      }));
    } else {
      setMode("bc");
      setFields((prevFields) => ({
        ...prevFields,
        allowed_bc: true,
      }));
    }
  };

  const handleToggleMcl = () => {
    if (mode !== "mcl") {
      setMode("mcl");
      setFields((prevFields) => ({
        ...prevFields,
        allowed_mcl: true,
      }));
    } else {
      setFields((prevFields) => ({
        ...prevFields,
        allowed_mcl: !prevFields?.allowed_mcl,
      }));
    }
  };

  const handleToggleUCL = () => {
    if (mode !== "ucl") {
      setMode("ucl");
      setFields((prevFields) => ({
        ...prevFields,
        allowed_ucl: true,
      }));
    } else {
      setFields((prevFields) => ({
        ...prevFields,
        allowed_ucl: !prevFields?.allowed_ucl,
      }));
    }
  };

  const handleToggleAided = () => {
    if (mode !== "aided") {
      setMode("aided");
      setFields((prevFields) => ({
        ...prevFields,
        allowed_aided: true,
      }));
    } else {
      setFields((prevFields) => ({
        ...prevFields,
        allowed_aided: !prevFields?.allowed_aided,
      }));
    }
  };

  const handleResetRightChart = () => {
    if (mode === "ac" || mode === "ac-nh") {
      setRightAcData(new Array(11).fill(null));
      setFields((prev) => ({
        ...prev,
        rf1: null,
        rf2: null,
        rf3: null,
        rf3_5: null,
        rf4: null,
        rf4_5: null,
        rf5: null,
        rf5_5: null,
        rf6: null,
        rf6_5: null,
        rf7: null,
        ac_r_125_nh: null,
        ac_r_250_nh: null,
        ac_r_500_nh: null,
        ac_r_750_nh: null,
        ac_r_1000_nh: null,
        ac_r_1500_nh: null,
        ac_r_2000_nh: null,
        ac_r_3000_nh: null,
        ac_r_4000_nh: null,
        ac_r_6000_nh: null,
        ac_r_8000_nh: null,
      }));
    } else if (mode === "bc" || mode === "bc-nh") {
      setRightBcData(new Array(11).fill(null));
      setFields((prev) => ({
        ...prev,
        brf1: null,
        brf2: null,
        brf3: null,
        brf3_5: null,
        brf4: null,
        brf4_5: null,
        brf5: null,
        brf5_5: null,
        brf6: null,
        brf6_5: null,
        brf7: null,
        bc_r_125_nh: null,
        bc_r_250_nh: null,
        bc_r_500_nh: null,
        bc_r_750_nh: null,
        bc_r_1000_nh: null,
        bc_r_1500_nh: null,
        bc_r_2000_nh: null,
        bc_r_3000_nh: null,
        bc_r_4000_nh: null,
        bc_r_6000_nh: null,
        bc_r_8000_nh: null,
      }));
    } else if (mode === "ucl") {
      setRightUclData(new Array(11).fill(null));
      setFields((prev) => ({
        ...prev,
        urf1: null,
        urf2: null,
        urf3: null,
        urf3_5: null,
        urf4: null,
        urf4_5: null,
        urf5: null,
        urf5_5: null,
        urf6: null,
        urf6_5: null,
        urf7: null,
        urf8: null,
      }));
    } else if (mode === "mcl") {
      setRightMclData(new Array(11).fill(null));
      setFields((prev) => ({
        ...prev,
        mrf1: null,
        mrf2: null,
        mrf3: null,
        mrf3_5: null,
        mrf4: null,
        mrf4_5: null,
        mrf5: null,
        mrf5_5: null,
        mrf6: null,
        mrf6_5: null,
        mrf7: null,
        mrf8: null,
      }));
    } else if (mode === "aided") {
      setRightAidedData(new Array(11).fill(null));
      setFields((prev) => ({
        ...prev,
        aided_rf1: null,
        aided_rf2: null,
        aided_rf3: null,
        aided_rf3_5: null,
        aided_rf4: null,
        aided_rf4_5: null,
        aided_rf5: null,
        aided_rf5_5: null,
        aided_rf6: null,
        aided_rf6_5: null,
        aided_rf7: null,
      }));
    }
  };

  const handleResetLeftChart = () => {
    if (mode === "ac" || mode === "ac-nh") {
      setLeftAcData(new Array(11).fill(null));
      setFields((prev) => ({
        ...prev,
        lf1: null,
        lf2: null,
        lf3: null,
        lf3_5: null,
        lf4: null,
        lf4_5: null,
        lf5: null,
        lf5_5: null,
        lf6: null,
        lf6_5: null,
        lf7: null,
        ac_l_125_nh: null,
        ac_l_250_nh: null,
        ac_l_500_nh: null,
        ac_l_750_nh: null,
        ac_l_1000_nh: null,
        ac_l_1500_nh: null,
        ac_l_2000_nh: null,
        ac_l_3000_nh: null,
        ac_l_4000_nh: null,
        ac_l_6000_nh: null,
        ac_l_8000_nh: null,
      }));
    } else if (mode === "bc" || mode === "bc-nh") {
      setLeftBcData(new Array(11).fill(null));
      setFields((prev) => ({
        ...prev,
        blf1: null,
        blf2: null,
        blf3: null,
        blf3_5: null,
        blf4: null,
        blf4_5: null,
        blf5: null,
        blf5_5: null,
        blf6: null,
        blf6_5: null,
        blf7: null,
        bc_l_125_nh: null,
        bc_l_250_nh: null,
        bc_l_500_nh: null,
        bc_l_750_nh: null,
        bc_l_1000_nh: null,
        bc_l_1500_nh: null,
        bc_l_2000_nh: null,
        bc_l_3000_nh: null,
        bc_l_4000_nh: null,
        bc_l_6000_nh: null,
        bc_l_8000_nh: null,
      }));
    } else if (mode === "ucl") {
      setLeftUclData(new Array(11).fill(null));
      setFields((prev) => ({
        ...prev,
        ulf1: null,
        ulf2: null,
        ulf3: null,
        ulf3_5: null,
        ulf4: null,
        ulf4_5: null,
        ulf5: null,
        ulf5_5: null,
        ulf6: null,
        ulf6_5: null,
        ulf7: null,
        ulf8: null,
      }));
    } else if (mode === "mcl") {
      setLeftMclData(new Array(11).fill(null));
      setFields((prev) => ({
        ...prev,
        mlf1: null,
        mlf2: null,
        mlf3: null,
        mlf3_5: null,
        mlf4: null,
        mlf4_5: null,
        mlf5: null,
        mlf5_5: null,
        mlf6: null,
        mlf6_5: null,
        mlf7: null,
        mlf8: null,
      }));
    } else if (mode === "aided") {
      setLeftAidedData(new Array(11).fill(null));
      setFields((prev) => ({
        ...prev,
        aided_lf1: null,
        aided_lf2: null,
        aided_lf3: null,
        aided_lf3_5: null,
        aided_lf4: null,
        aided_lf4_5: null,
        aided_lf5: null,
        aided_lf5_5: null,
        aided_lf6: null,
        aided_lf6_5: null,
        aided_lf7: null,
      }));
    }
  };

  const handleValueChange = (index, value, mode, ear, isCtrlPressed) => {
    // If receiving isCtrlPressed, ignore passed `mode` for NH logic, except as base mode
    let effectiveMode = mode;
    if (isCtrlPressed) {
      if (mode === "ac") effectiveMode = "ac-nh";
      else if (mode === "bc") effectiveMode = "bc-nh";
    }

    const keySuffixes = [
      "1",
      "2",
      "3",
      "3_5",
      "4",
      "4_5",
      "5",
      "5_5",
      "6",
      "6_5",
      "7",
    ];

    const keyindexSuffixes = [
      "125",
      "250",
      "500",
      "750",
      "1000",
      "1500",
      "2000",
      "3000",
      "4000",
      "6000",
      "8000",
    ];

    const suffix = keySuffixes[index];
    if (!suffix) return;
    const suffixindex = keyindexSuffixes[index];
    if (!suffixindex) return;

    const isNH = effectiveMode === "ac-nh" || effectiveMode === "bc-nh";

    let key;

    if (isNH) {
      if (effectiveMode === "ac-nh") {
        key = `${ear === "right" ? "ac_r_" : "ac_l_"}${suffixindex}_nh`;
      } else {
        key = `${ear === "right" ? "bc_r_" : "bc_l_"}${suffixindex}_nh`;
      }
    } else {
      const prefix =
        effectiveMode === "bc"
          ? "b"
          : effectiveMode === "ac"
            ? ""
            : effectiveMode === "ucl"
              ? "u"
              : effectiveMode === "aided"
                ? "aided_"
                : "m";

      key = `${prefix}${ear === "right" ? "rf" : "lf"}${suffix}`;
    }

    const newData = { ...fields };
    const toggledValue = newData[key] === value ? null : value;
    newData[key] = toggledValue;

    if (toggledValue !== null) {
      if (effectiveMode === "ac") {
        const nhKey = `${ear === "right" ? "ac_r_" : "ac_l_"}${suffixindex}_nh`;
        newData[nhKey] = null;
      } else if (effectiveMode === "ac-nh") {
        const acKey = `${ear === "right" ? "rf" : "lf"}${suffix}`;
        newData[acKey] = null;
        if (ear === "right") {
          const updated = [...rightAcData];
          updated[index] = null;
          setRightAcData(updated);
        } else {
          const updated = [...leftAcData];
          updated[index] = null;
          setLeftAcData(updated);
        }
      } else if (effectiveMode === "bc") {
        const nhKey = `${ear === "right" ? "bc_r_" : "bc_l_"}${suffixindex}_nh`;
        newData[nhKey] = null;
      } else if (effectiveMode === "bc-nh") {
        const bcKey = `b${ear === "right" ? "rf" : "lf"}${suffix}`;
        newData[bcKey] = null;
        if (ear === "right") {
          const updated = [...rightBcData];
          updated[index] = null;
          setRightBcData(updated);
        } else {
          const updated = [...leftBcData];
          updated[index] = null;
          setLeftBcData(updated);
        }
      }
    }

    const updateArray = (data, setter) => {
      const updated = [...data];
      updated[index] = toggledValue;
      setter(updated);
    };

    if (ear === "right") {
      if (effectiveMode === "ac") updateArray(rightAcData, setRightAcData);
      else if (effectiveMode === "bc") updateArray(rightBcData, setRightBcData);
      else if (effectiveMode === "ucl")
        updateArray(rightUclData, setRightUclData);
      else if (effectiveMode === "mcl")
        updateArray(rightMclData, setRightMclData);
      else if (effectiveMode === "aided")
        updateArray(rightAidedData, setRightAidedData);
    } else {
      if (effectiveMode === "ac") updateArray(leftAcData, setLeftAcData);
      else if (effectiveMode === "bc") updateArray(leftBcData, setLeftBcData);
      else if (effectiveMode === "ucl")
        updateArray(leftUclData, setLeftUclData);
      else if (effectiveMode === "mcl")
        updateArray(leftMclData, setLeftMclData);
      else if (effectiveMode === "aided")
        updateArray(leftAidedData, setLeftAidedData);
    }

    setFields(newData);
  };

  const handleOscillationValueChange = (index, value, oscillaMode, ear) => {
    const labels = [
      "125",
      "250",
      "500",
      "750",
      "1000",
      "1500",
      "2000",
      "3000",
      "4000",
      "6000",
      "8000",
    ];
    const freq = labels[index];
    const side = ear[0];

    let fieldName = "";
    let oppositeFieldName = "";

    if (oscillaMode === "ac Heard") {
      fieldName = `ac_${side}_${freq}`;
      oppositeFieldName = `ac_${side}_${freq}_nh`;
    } else if (oscillaMode === "ac Not Heard") {
      fieldName = `ac_${side}_${freq}_nh`;
      oppositeFieldName = `ac_${side}_${freq}`;
    } else if (oscillaMode === "bc Heard") {
      fieldName = `bc_${side}_${freq}`;
      oppositeFieldName = `bc_${side}_${freq}_nh`;
    } else if (oscillaMode === "bc Not Heard") {
      fieldName = `bc_${side}_${freq}_nh`;
      oppositeFieldName = `bc_${side}_${freq}`;
    }

    setOscillaAudiogramState((prev) => ({
      ...prev,
      [fieldName]: value,
      [oppositeFieldName]: undefined,
    }));
  };

  const clamp = (val, num) => {
    if (val == null) return null;
    return Math.min(120, val + num);
  };

  return (
    <>
      {testStarted &&
        ReactDOM.createPortal(
          <Backdrop
            open={testStarted}
            sx={{
              zIndex: 99999,
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.65)",
              backdropFilter: "blur(2px)",
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={() => {
                dispatch(startOscillaLoadingValues(false));
              }}
              sx={{
                position: "fixed",
                top: 16,
                right: 16,
                zIndex: 100000,
                width: 48,
                height: 48,
                backgroundColor: "#fff",
                color: "#666",
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Loader */}
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{ pointerEvents: "none" }}
            >
              <CircularProgress size={50} />
            </Box>
          </Backdrop>,
          document.body,
        )}
      <Container maxWidth="xl">
        <Grid
          container
          // mt={3}
          sx={{
            height: "85vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          <Grid item xs={5} md={4.9}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box
                // pb={5}
                sx={{
                  backgroundColor: "#FFF6F6",
                  height: "78vh",
                  maxWidth: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  <Typography color="error" variant="h4" ml={4}>
                    • Right
                  </Typography>
                </Box>

                {oscillaAudiogramState.allowed_Oscilla ? (
                  <OscillaChartComponent
                    rightEar
                    optional={true}
                    oscillaMode={oscillaMode}
                    heardUCL={[
                      oscillaAudiogramState?.ucl_r_125,
                      oscillaAudiogramState?.ucl_r_250,
                      oscillaAudiogramState?.ucl_r_500,
                      oscillaAudiogramState?.ucl_r_750,
                      oscillaAudiogramState?.ucl_r_1000,
                      oscillaAudiogramState?.ucl_r_1500,
                      oscillaAudiogramState?.ucl_r_2000,
                      oscillaAudiogramState?.ucl_r_3000,
                      oscillaAudiogramState?.ucl_r_4000,
                      oscillaAudiogramState?.ucl_r_6000,
                      oscillaAudiogramState?.ucl_r_8000,
                    ]}
                    heardMCL={[
                      oscillaAudiogramState?.mcl_r_125,
                      oscillaAudiogramState?.mcl_r_250,
                      oscillaAudiogramState?.mcl_r_500,
                      oscillaAudiogramState?.mcl_r_750,
                      oscillaAudiogramState?.mcl_r_1000,
                      oscillaAudiogramState?.mcl_r_1500,
                      oscillaAudiogramState?.mcl_r_2000,
                      oscillaAudiogramState?.mcl_r_3000,
                      oscillaAudiogramState?.mcl_r_4000,
                      oscillaAudiogramState?.mcl_r_6000,
                      oscillaAudiogramState?.mcl_r_8000,
                    ]}
                    n_heardUCL={[
                      oscillaAudiogramState?.ucl_r_125_nh,
                      oscillaAudiogramState?.ucl_r_250_nh,
                      oscillaAudiogramState?.ucl_r_500_nh,
                      oscillaAudiogramState?.ucl_r_750_nh,
                      oscillaAudiogramState?.ucl_r_1000_nh,
                      oscillaAudiogramState?.ucl_r_1500_nh,
                      oscillaAudiogramState?.ucl_r_2000_nh,
                      oscillaAudiogramState?.ucl_r_3000_nh,
                      oscillaAudiogramState?.ucl_r_4000_nh,
                      oscillaAudiogramState?.ucl_r_6000_nh,
                      oscillaAudiogramState?.ucl_r_8000_nh,
                    ]}
                    n_heardMCL={[
                      oscillaAudiogramState?.mcl_r_125_nh,
                      oscillaAudiogramState?.mcl_r_250_nh,
                      oscillaAudiogramState?.mcl_r_500_nh,
                      oscillaAudiogramState?.mcl_r_750_nh,
                      oscillaAudiogramState?.mcl_r_1000_nh,
                      oscillaAudiogramState?.mcl_r_1500_nh,
                      oscillaAudiogramState?.mcl_r_2000_nh,
                      oscillaAudiogramState?.mcl_r_3000_nh,
                      oscillaAudiogramState?.mcl_r_4000_nh,
                      oscillaAudiogramState?.mcl_r_6000_nh,
                      oscillaAudiogramState?.mcl_r_8000_nh,
                    ]}
                    heardAC={[
                      oscillaAudiogramState?.ac_r_125,
                      oscillaAudiogramState?.ac_r_250,
                      oscillaAudiogramState?.ac_r_500,
                      oscillaAudiogramState?.ac_r_750,
                      oscillaAudiogramState?.ac_r_1000,
                      oscillaAudiogramState?.ac_r_1500,
                      oscillaAudiogramState?.ac_r_2000,
                      oscillaAudiogramState?.ac_r_3000,
                      oscillaAudiogramState?.ac_r_4000,
                      oscillaAudiogramState?.ac_r_6000,
                      oscillaAudiogramState?.ac_r_8000,
                    ]}
                    heardBC={[
                      oscillaAudiogramState?.bc_r_125,
                      oscillaAudiogramState?.bc_r_250,
                      oscillaAudiogramState?.bc_r_500,
                      oscillaAudiogramState?.bc_r_750,
                      oscillaAudiogramState?.bc_r_1000,
                      oscillaAudiogramState?.bc_r_1500,
                      oscillaAudiogramState?.bc_r_2000,
                      oscillaAudiogramState?.bc_r_3000,
                      oscillaAudiogramState?.bc_r_4000,
                      oscillaAudiogramState?.bc_r_6000,
                      oscillaAudiogramState?.bc_r_8000,
                    ]}
                    n_heardAC={[
                      oscillaAudiogramState?.ac_r_125_nh,
                      oscillaAudiogramState?.ac_r_250_nh,
                      oscillaAudiogramState?.ac_r_500_nh,
                      oscillaAudiogramState?.ac_r_750_nh,
                      oscillaAudiogramState?.ac_r_1000_nh,
                      oscillaAudiogramState?.ac_r_1500_nh,
                      oscillaAudiogramState?.ac_r_2000_nh,
                      oscillaAudiogramState?.ac_r_3000_nh,
                      oscillaAudiogramState?.ac_r_4000_nh,
                      oscillaAudiogramState?.ac_r_6000_nh,
                      oscillaAudiogramState?.ac_r_8000_nh,
                    ]}
                    n_heardBC={[
                      oscillaAudiogramState?.bc_r_125_nh,
                      oscillaAudiogramState?.bc_r_250_nh,
                      oscillaAudiogramState?.bc_r_500_nh,
                      oscillaAudiogramState?.bc_r_750_nh,
                      oscillaAudiogramState?.bc_r_1000_nh,
                      oscillaAudiogramState?.bc_r_1500_nh,
                      oscillaAudiogramState?.bc_r_2000_nh,
                      oscillaAudiogramState?.bc_r_3000_nh,
                      oscillaAudiogramState?.bc_r_4000_nh,
                      oscillaAudiogramState?.bc_r_6000_nh,
                      oscillaAudiogramState?.bc_r_8000_nh,
                    ]}
                  />
                ) : (
                  <ChartComponent
                    only_view={only_view}
                    allowed_ac={fields?.allowed_ac}
                    allowed_bc={fields?.allowed_bc}
                    allowed_ucl={fields?.allowed_ucl}
                    allowed_mcl={fields?.allowed_mcl}
                    allowed_aided={fields?.allowed_aided}
                    onValueChange={(index, value, mode, isCtrlPressed) => {
                    !only_view&&  handleValueChange(
                        index,
                        value,
                        mode,
                        "right",
                        isCtrlPressed,
                      );
                    }}
                    n_heardAC={[
                      fields?.ac_r_125_nh,
                      fields?.ac_r_250_nh,
                      fields?.ac_r_500_nh,
                      fields?.ac_r_750_nh,
                      fields?.ac_r_1000_nh,
                      fields?.ac_r_1500_nh,
                      fields?.ac_r_2000_nh,
                      fields?.ac_r_3000_nh,
                      fields?.ac_r_4000_nh,
                      fields?.ac_r_6000_nh,
                      fields?.ac_r_8000_nh,
                    ]}
                    n_heardBC={[
                      fields?.bc_r_125_nh,
                      fields?.bc_r_250_nh,
                      fields?.bc_r_500_nh,
                      fields?.bc_r_750_nh,
                      fields?.bc_r_1000_nh,
                      fields?.bc_r_1500_nh,
                      fields?.bc_r_2000_nh,
                      fields?.bc_r_3000_nh,
                      fields?.bc_r_4000_nh,
                      fields?.bc_r_6000_nh,
                      fields?.bc_r_8000_nh,
                    ]}
                    data={rightAcData}
                    title="AC"
                    boneData={rightBcData}
                    boneTitle="BC"
                    UclData={rightUclData}
                    UclTitle="UCL"
                    MclData={rightMclData}
                    aidedData={rightAidedData}
                    MclTitle="MCL"
                    AidedTitle="Aided"
                    rightEar
                    mode={mode}
                  />
                )}
              </Box>
              {/* <Box mt={3}>
                {fields?.allowed_aided && (
                  <Grid container spacing={0}>
                    <FrequencyBoxComponent
                      isFirst
                      title="Frequency (Hz)"
                      onlyView
                      value="AC"
                      value_bone="BC"
                      value_aided="Aided"
                      allowed_ac={fields.allowed_ac}
                      allowed_bc={fields.allowed_bc}
                      allowed_aided={fields.allowed_aided}
                      direction="right"
                    />

                    <FrequencyBoxComponent
                      direction="right"
                      title="500"
                      onlyView={only_view}
                      value={Math.abs(fields.rf1)}
                      value_bone={fields.brf3}
                      value_aided={fields.aided_rf3}
                      allowed_ac={fields.allowed_ac}
                      allowed_bc={fields.allowed_bc}
                      allowed_aided={fields.allowed_aided}
                      accessThreshold={accessThreshold[0]}
                    />
                    <FrequencyBoxComponent
                      direction="right"
                      title="1k"
                      onlyView={only_view}
                      value={fields.rf4}
                      value_bone={fields.brf4}
                      value_aided={fields.aided_rf4}
                      allowed_ac={fields.allowed_ac}
                      allowed_bc={fields.allowed_bc}
                      allowed_aided={fields.allowed_aided}
                      accessThreshold={accessThreshold[1]}
                    />
                    <FrequencyBoxComponent
                      direction="right"
                      title="2k"
                      onlyView={only_view}
                      value={fields.rf5}
                      value_bone={fields.brf5}
                      value_aided={fields.aided_rf5}
                      allowed_ac={fields.allowed_ac}
                      allowed_bc={fields.allowed_bc}
                      allowed_aided={fields.allowed_aided}
                      onChange={(val) => setFields({ ...fields, rf5: val })}
                      accessThreshold={accessThreshold[2]}
                    />
                    <FrequencyBoxComponent
                      direction="right"
                      title="4k"
                      isLast={true}
                      onlyView={only_view}
                      value={fields.rf6}
                      value_bone={fields.brf6}
                      value_aided={fields.aided_rf6}
                      allowed_ac={fields.allowed_ac}
                      allowed_bc={fields.allowed_bc}
                      allowed_aided={fields.allowed_aided}
                      accessThreshold={accessThreshold[3]}
                    />
                  </Grid>
                )}
              </Box> */}
            </Box>
          </Grid>

          {oscillaAudiogramState.allowed_Oscilla ? (
            <Grid direction={"column"} item md={0.8} xs={1}></Grid>
          ) : (
            !only_view &&
            !loading && (
              <Grid
                direction={"column"}
                item
                md={2}
                xs={1}
                display={"flex"}
                flexDirection={"column"}
                justifyContent={"center"}
                alignItems={"center"}
              >
                <Grid
                  item
                  md={1}
                  xs={1}
                  mt={2}
                  sx={{
                    display: "flex",
                    flex: 1,
                    justifyContent: "center",
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Tooltip title="Ac Threshold" placement="right">
                    <Button
                      onClick={handleToggleAC}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        backgroundColor:
                          mode === "ac" || mode === "ac-nh"
                            ? "#EDF0F7"
                            : "transparent",
                        border:
                          mode === "ac" || mode === "ac-nh"
                            ? "1px solid #1265A1"
                            : "1px solid #EDF0F7",
                        minWidth: "auto",
                        // padding: "8px 12px",
                        p: 3,
                      }}
                    >
                      {mode === "ac-nh" ? (
                        <>
                          <Box
                            component="img"
                            src={AirNotHeardRightIMG}
                            alt="AC-NH-Right"
                            sx={{
                              width: 17,
                              height: 17,
                              objectFit: "contain",
                            }}
                          />
                          <Box
                            sx={{ border: "1px solid #C24747", height: "14px" }}
                          ></Box>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 550,
                              whiteSpace: "nowrap",
                              color: "#2D3B67",
                            }}
                          >
                            AC-NH
                          </Typography>
                          <Box
                            sx={{ border: "1px solid #1265A1", height: "14px" }}
                          ></Box>
                          <Box
                            component="img"
                            src={AirNotHeardLeftIMG}
                            alt="AC-NH-Left"
                            sx={{
                              width: 17,
                              height: 17,
                              objectFit: "contain",
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <Box
                            component="img"
                            src={AcHeardRightIMG}
                            alt="AC-H-Right"
                            sx={{
                              width: 17,
                              height: 17,
                              objectFit: "contain",
                            }}
                          />
                          <Box
                            sx={{ border: "1px solid #C24747", height: "14px" }}
                          ></Box>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 550,
                              whiteSpace: "nowrap",
                              color: "#2D3B67",
                              mr: 1,
                              ml: 1,
                            }}
                          >
                            AC
                          </Typography>
                          <Box
                            sx={{ border: "1px solid #1265A1", height: "14px" }}
                          ></Box>
                          <Box
                            component="img"
                            src={ACHeardLeftIMG}
                            alt="AC-H-Left"
                            sx={{
                              width: 17,
                              height: 17,
                              objectFit: "contain",
                            }}
                          />
                        </>
                      )}
                    </Button>
                  </Tooltip>
                </Grid>
                {/* <Grid
                  item
                  md={1}
                  xs={1}
                  sx={{
                    display: "flex",
                    flex: 1,
                    justifyContent: "center",
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Button
                    onClick={handleToggleBC}
                    sx={{ backgroundColor: mode === "bc" ? "#EDF0F7" : "" }}
                  >
                    <img
                      src={BCIMG}
                      alt="BC"
                      style={{ maxHeight: "100%", maxWidth: "100%" }}
                    />
                  </Button>
                </Grid> */}
                <Grid
                  item
                  md={1}
                  xs={1}
                  mt={2}
                  sx={{
                    display: "flex",
                    flex: 1,
                    justifyContent: "center",
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Tooltip title="BC Threshold" placement="right">
                    <Button
                      onClick={handleToggleBC}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        backgroundColor:
                          mode === "bc" || mode === "bc-nh"
                            ? "#EDF0F7"
                            : "transparent",
                        border:
                          mode === "bc" || mode === "bc-nh"
                            ? "1px solid #1265A1"
                            : "1px solid #EDF0F7",
                        minWidth: "auto",
                        // padding: "8px 12px",
                        ...(mode === "bc-nh"
                          ? { pt: 3, pb: 3, pl: 1, pr: 1 }
                          : { p: 3 }),
                      }}
                    >
                      {mode === "bc-nh" ? (
                        <>
                          <Box
                            component="img"
                            src={BoneNotHeardRightIMG}
                            alt="BC-NH-Right"
                            sx={{
                              width: 24,
                              height: 24,
                              objectFit: "contain",
                            }}
                          />
                          <Box
                            sx={{ border: "1px solid #C24747", height: "14px" }}
                          ></Box>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 550,
                              whiteSpace: "nowrap",
                              color: "#2D3B67",
                            }}
                          >
                            BC-NH
                          </Typography>
                          <Box
                            sx={{ border: "1px solid #1265A1", height: "14px" }}
                          ></Box>
                          <Box
                            component="img"
                            src={BoneNotHeardLeftIMG}
                            alt="BC-NH-Left"
                            sx={{
                              width: 24,
                              height: 24,
                              objectFit: "contain",
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <Box
                            component="img"
                            src={RIGHTARROWICON}
                            alt="BC-H-Right"
                            sx={{
                              width: 17,
                              height: 17,
                              objectFit: "contain",
                            }}
                          />
                          <Box
                            sx={{ border: "1px solid #C24747", height: "14px" }}
                          ></Box>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 550,
                              whiteSpace: "nowrap",
                              color: "#2D3B67",
                              mr: 1,
                              ml: 1,
                            }}
                          >
                            BC
                          </Typography>
                          <Box
                            sx={{ border: "1px solid #1265A1", height: "14px" }}
                          ></Box>
                          <Box
                            component="img"
                            src={LEFTARROWICON}
                            alt="BC-H-Left"
                            sx={{
                              width: 17,
                              height: 17,
                              objectFit: "contain",
                            }}
                          />
                        </>
                      )}
                    </Button>
                  </Tooltip>
                </Grid>

                <Box
                  sx={{
                    flex: "0",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Tooltip title="Mirror Right to Left" placement="left">
                    <Button onClick={() => handleMirrorData_r_to_l()}>
                      <img
                        src={ARROW_RIGHT}
                        alt="Mirror R to L"
                        style={{ height: "40px" }}
                      />
                    </Button>
                  </Tooltip>
                  <Tooltip title="Mirror Left to Right" placement="right">
                    <Button onClick={() => handleMirrorData_l_to_r()}>
                      <img
                        src={ARROW_LEFT}
                        alt="Mirror L to R"
                        style={{ height: "40px" }}
                      />
                    </Button>
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "48px",
                  }}
                >
                  <Tooltip title="Reset Right Side Threshold" placement="left">
                    <Button onClick={handleResetRightChart}>
                      <img
                        src={RESET_RIGHT}
                        alt="Reset Right"
                        style={{ height: "40px" }}
                      />
                    </Button>
                  </Tooltip>
                  <Tooltip title="Reset Left Side Threshold" placement="right">
                    <Button onClick={handleResetLeftChart}>
                      <img
                        src={RESET_LEFT}
                        alt="Reset Left"
                        style={{ height: "40px" }}
                      />
                    </Button>
                  </Tooltip>
                </Box>
              </Grid>
            )
          )}

          <Grid item xs={5} md={4.9}>
            <Box
              // pb={5}
              sx={{
                backgroundColor: "#F6FAFF",
                maxWidth: "100%",
                height: "78vh",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                <Typography color="primary" variant="h4" ml={4}>
                  • Left
                </Typography>
              </Box>
              {oscillaAudiogramState.allowed_Oscilla ? (
                <OscillaChartComponent
                  optional={true}
                  oscillaMode={oscillaMode}
                  heardUCL={[
                    oscillaAudiogramState?.ucl_l_125,
                    oscillaAudiogramState?.ucl_l_250,
                    oscillaAudiogramState?.ucl_l_500,
                    oscillaAudiogramState?.ucl_l_750,
                    oscillaAudiogramState?.ucl_l_1000,
                    oscillaAudiogramState?.ucl_l_1500,
                    oscillaAudiogramState?.ucl_l_2000,
                    oscillaAudiogramState?.ucl_l_3000,
                    oscillaAudiogramState?.ucl_l_4000,
                    oscillaAudiogramState?.ucl_l_6000,
                    oscillaAudiogramState?.ucl_l_8000,
                  ]}
                  heardMCL={[
                    oscillaAudiogramState?.mcl_l_125,
                    oscillaAudiogramState?.mcl_l_250,
                    oscillaAudiogramState?.mcl_l_500,
                    oscillaAudiogramState?.mcl_l_750,
                    oscillaAudiogramState?.mcl_l_1000,
                    oscillaAudiogramState?.mcl_l_1500,
                    oscillaAudiogramState?.mcl_l_2000,
                    oscillaAudiogramState?.mcl_l_3000,
                    oscillaAudiogramState?.mcl_l_4000,
                    oscillaAudiogramState?.mcl_l_6000,
                    oscillaAudiogramState?.mcl_l_8000,
                  ]}
                  n_heardUCL={[
                    oscillaAudiogramState?.ucl_l_125_nh,
                    oscillaAudiogramState?.ucl_l_250_nh,
                    oscillaAudiogramState?.ucl_l_500_nh,
                    oscillaAudiogramState?.ucl_l_750_nh,
                    oscillaAudiogramState?.ucl_l_1000_nh,
                    oscillaAudiogramState?.ucl_l_1500_nh,
                    oscillaAudiogramState?.ucl_l_2000_nh,
                    oscillaAudiogramState?.ucl_l_3000_nh,
                    oscillaAudiogramState?.ucl_l_4000_nh,
                    oscillaAudiogramState?.ucl_l_6000_nh,
                    oscillaAudiogramState?.ucl_l_8000_nh,
                  ]}
                  n_heardMCL={[
                    oscillaAudiogramState?.mcl_l_125_nh,
                    oscillaAudiogramState?.mcl_l_250_nh,
                    oscillaAudiogramState?.mcl_l_500_nh,
                    oscillaAudiogramState?.mcl_l_750_nh,
                    oscillaAudiogramState?.mcl_l_1000_nh,
                    oscillaAudiogramState?.mcl_l_1500_nh,
                    oscillaAudiogramState?.mcl_l_2000_nh,
                    oscillaAudiogramState?.mcl_l_3000_nh,
                    oscillaAudiogramState?.mcl_l_4000_nh,
                    oscillaAudiogramState?.mcl_l_6000_nh,
                    oscillaAudiogramState?.mcl_l_8000_nh,
                  ]}
                  heardAC={[
                    oscillaAudiogramState?.ac_l_125,
                    oscillaAudiogramState?.ac_l_250,
                    oscillaAudiogramState?.ac_l_500,
                    oscillaAudiogramState?.ac_l_750,
                    oscillaAudiogramState?.ac_l_1000,
                    oscillaAudiogramState?.ac_l_1500,
                    oscillaAudiogramState?.ac_l_2000,
                    oscillaAudiogramState?.ac_l_3000,
                    oscillaAudiogramState?.ac_l_4000,
                    oscillaAudiogramState?.ac_l_6000,
                    oscillaAudiogramState?.ac_l_8000,
                  ]}
                  heardBC={[
                    oscillaAudiogramState?.bc_l_125,
                    oscillaAudiogramState?.bc_l_250,
                    oscillaAudiogramState?.bc_l_500,
                    oscillaAudiogramState?.bc_l_750,
                    oscillaAudiogramState?.bc_l_1000,
                    oscillaAudiogramState?.bc_l_1500,
                    oscillaAudiogramState?.bc_l_2000,
                    oscillaAudiogramState?.bc_l_3000,
                    oscillaAudiogramState?.bc_l_4000,
                    oscillaAudiogramState?.bc_l_6000,
                    oscillaAudiogramState?.bc_l_8000,
                  ]}
                  n_heardAC={[
                    oscillaAudiogramState?.ac_l_125_nh,
                    oscillaAudiogramState?.ac_l_250_nh,
                    oscillaAudiogramState?.ac_l_500_nh,
                    oscillaAudiogramState?.ac_l_750_nh,
                    oscillaAudiogramState?.ac_l_1000_nh,
                    oscillaAudiogramState?.ac_l_1500_nh,
                    oscillaAudiogramState?.ac_l_2000_nh,
                    oscillaAudiogramState?.ac_l_3000_nh,
                    oscillaAudiogramState?.ac_l_4000_nh,
                    oscillaAudiogramState?.ac_l_6000_nh,
                    oscillaAudiogramState?.ac_l_8000_nh,
                  ]}
                  n_heardBC={[
                    oscillaAudiogramState?.bc_l_125_nh,
                    oscillaAudiogramState?.bc_l_250_nh,
                    oscillaAudiogramState?.bc_l_500_nh,
                    oscillaAudiogramState?.bc_l_750_nh,
                    oscillaAudiogramState?.bc_l_1000_nh,
                    oscillaAudiogramState?.bc_l_1500_nh,
                    oscillaAudiogramState?.bc_l_2000_nh,
                    oscillaAudiogramState?.bc_l_3000_nh,
                    oscillaAudiogramState?.bc_l_4000_nh,
                    oscillaAudiogramState?.bc_l_6000_nh,
                    oscillaAudiogramState?.bc_l_8000_nh,
                  ]}
                />
              ) : (
                <ChartComponent
                  allowed_ac={fields?.allowed_ac}
                  allowed_bc={fields?.allowed_bc}
                  allowed_ucl={fields?.allowed_ucl}
                  allowed_mcl={fields?.allowed_mcl}
                  allowed_aided={fields?.allowed_aided}
                  onValueChange={(index, value, mode, isCtrlPressed) => {
                   !only_view && handleValueChange(
                      index,
                      value,
                      mode,
                      "left",
                      isCtrlPressed,
                    );
                  }}
                  n_heardAC={[
                    fields?.ac_l_125_nh,
                    fields?.ac_l_250_nh,
                    fields?.ac_l_500_nh,
                    fields?.ac_l_750_nh,
                    fields?.ac_l_1000_nh,
                    fields?.ac_l_1500_nh,
                    fields?.ac_l_2000_nh,
                    fields?.ac_l_3000_nh,
                    fields?.ac_l_4000_nh,
                    fields?.ac_l_6000_nh,
                    fields?.ac_l_8000_nh,
                  ]}
                  n_heardBC={[
                    fields?.bc_l_125_nh,
                    fields?.bc_l_250_nh,
                    fields?.bc_l_500_nh,
                    fields?.bc_l_750_nh,
                    fields?.bc_l_1000_nh,
                    fields?.bc_l_1500_nh,
                    fields?.bc_l_2000_nh,
                    fields?.bc_l_3000_nh,
                    fields?.bc_l_4000_nh,
                    fields?.bc_l_6000_nh,
                    fields?.bc_l_8000_nh,
                  ]}
                  data={leftAcData}
                  title="AC"
                  boneData={leftBcData}
                  boneTitle="BC"
                  UclTitle="UCL"
                  UclData={leftUclData}
                  MclTitle="MCL"
                  MclData={leftMclData}
                  rightEar={false}
                  mode={mode}
                  aidedData={leftAidedData}
                  AidedTitle="Aided"
                />
              )}
            </Box>
            <Box>
              {/* <Box mt={3}>
                {fields.allowed_aided && (
                  <Grid container spacing={0}>
                    <FrequencyBoxComponent
                      isFirst
                      title="Frequency (Hz)"
                      onlyView
                      value="AC"
                      value_bone="BC"
                      value_aided="Aided"
                      allowed_ac={fields.allowed_ac}
                      allowed_bc={fields.allowed_bc}
                      allowed_aided={fields.allowed_aided}
                      direction="left"
                    />

                    <FrequencyBoxComponent
                      direction="left"
                      title="500"
                      onlyView={only_view}
                      value={fields.lf3}
                      value_bone={fields.blf3}
                      value_aided={fields.aided_lf3}
                      allowed_ac={fields.allowed_ac}
                      allowed_bc={fields.allowed_bc}
                      allowed_aided={fields.allowed_aided}
                      accessThreshold={accessThreshold[0]}
                    />
                    <FrequencyBoxComponent
                      direction="left"
                      title="1k"
                      onlyView={only_view}
                      value={fields.lf4}
                      value_bone={fields.blf4}
                      value_aided={fields.aided_lf4}
                      allowed_ac={fields.allowed_ac}
                      allowed_bc={fields.allowed_bc}
                      allowed_aided={fields.allowed_aided}
                      accessThreshold={accessThreshold[1]}
                    />
                    <FrequencyBoxComponent
                      direction="left"
                      title="2k"
                      onlyView={only_view}
                      value={fields.lf5}
                      value_bone={fields.blf5}
                      value_aided={fields.aided_lf5}
                      allowed_ac={fields.allowed_ac}
                      allowed_bc={fields.allowed_bc}
                      allowed_aided={fields.allowed_aided}
                      accessThreshold={accessThreshold[2]}
                    />
                    <FrequencyBoxComponent
                      direction="left"
                      title="4k"
                      isLast={true}
                      onlyView={only_view}
                      value={fields.lf6}
                      value_bone={fields.blf6}
                      value_aided={fields.aided_lf6}
                      allowed_ac={fields.allowed_ac}
                      allowed_bc={fields.allowed_bc}
                      allowed_aided={fields.allowed_aided}
                      accessThreshold={accessThreshold[3]}
                    />
                  </Grid>
                )}
              </Box> */}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default memo(AudioGram);
